import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  capitalizeFirstLetter,
  safeReplaceBredcrumb,
} from 'utils/helpers/string.manipulation';
import { clientThemePalette, CLIENT_THEME } from 'utils/clientTheme';
import { useAppSelector } from 'store/hook';
import { isDarkTheme } from 'utils/theme';
import { useTranslation } from 'react-i18next';
import { staticNavigationMap } from 'components/Route/staticNavigationMap';

const idToNavKey = (id: string): string =>
  id.replace(/([A-Z])/g, '_$1').toLowerCase();

const lastNonDynamicIndex = (templateSegments: string[]): number => {
  for (let i = templateSegments.length - 1; i >= 0; i--) {
    if (!templateSegments[i].startsWith(':')) return i;
  }
  return -1;
};

const matchTemplate = (
  templateSegments: string[],
  urlSegments: string[],
  offset: number
): boolean =>
  templateSegments.every(
    (seg, i) => seg.startsWith(':') || seg === urlSegments[offset + i]
  );

/** Maps each URL segment to a translation key derived from `staticNavigationMap`, or null when unresolved (falls back to raw-segment formatting). */
const resolveBreadcrumbNavKeys = (pathname: string): Array<string | null> => {
  const segments = pathname.split('/').filter(Boolean);
  const navKeys: Array<string | null> = new Array(segments.length).fill(null);

  const topLevelItems = [...staticNavigationMap].sort(
    (a, b) => b.path.split('/').length - a.path.split('/').length
  );

  let consumed = 0;
  const matchedTop = topLevelItems.find((item) => {
    const templateSegments = item.path.split('/');
    return (
      templateSegments.length <= segments.length - consumed &&
      matchTemplate(templateSegments, segments, consumed)
    );
  });

  if (!matchedTop) return navKeys;

  const topTemplateSegments = matchedTop.path.split('/');
  const topLabelIndex = lastNonDynamicIndex(topTemplateSegments);
  if (topLabelIndex >= 0) {
    navKeys[consumed + topLabelIndex] = idToNavKey(matchedTop.id);
  }
  consumed += topTemplateSegments.length;

  const child = matchedTop.children?.find((childItem) => {
    const templateSegments = childItem.path.split('/');
    return (
      templateSegments.length <= segments.length - consumed &&
      matchTemplate(templateSegments, segments, consumed)
    );
  });

  if (child) {
    const childTemplateSegments = child.path.split('/');
    const childLabelIndex = lastNonDynamicIndex(childTemplateSegments);
    if (childLabelIndex >= 0) {
      navKeys[consumed + childLabelIndex] = idToNavKey(child.id);
    }
  }

  return navKeys;
};

// Chevron icon as SVG for separator
const ChevronIcon = ({ color }: { color: string }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ margin: '0 4px' }}
    aria-hidden="true"
  >
    <path
      d="M9 18L15 12L9 6"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const BreadCrumb = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  const theme = useAppSelector((state) => state.authReducer).customer.theme;
  const enableDarkTheme = isDarkTheme(theme);
  const colorMode = enableDarkTheme ? 'dark' : 'light';
  const bc = clientThemePalette[CLIENT_THEME][colorMode];

  // Split by / and filter '' out
  const breadCrumbs = pathname.split('/').filter((x) => x);
  const navKeys = useMemo(() => resolveBreadcrumbNavKeys(pathname), [pathname]);

  // Handle responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <nav
      aria-label={t('breadcrumb')}
      className="breadcrumb-container"
      style={{
        display: 'flex',
        padding: '4px 8px',
        alignItems: 'center',
        backgroundColor: bc.breadcrumbBg,
      }}
    >
      {isSmallScreen && breadCrumbs.length > 2 && (
        <>
          {/* First crumb */}
          <BreadcrumbItem
            crumb={breadCrumbs[0]}
            navKey={navKeys[0]}
            isLast={false}
            activeColor={bc.breadcrumbActiveColor}
            inactiveColor={bc.breadcrumbInactiveColor}
          />
          <ChevronIcon color={bc.breadcrumbInactiveColor} />
          {/* Ellipsis to show truncation */}
          <span
            style={{
              color: bc.breadcrumbInactiveColor,
              margin: '0 4px',
              fontSize: '14px',
            }}
            aria-hidden="true"
          >
            …
          </span>
          <ChevronIcon color={bc.breadcrumbInactiveColor} />
          {/* Last crumb */}
          <BreadcrumbItem
            crumb={breadCrumbs[breadCrumbs.length - 1]}
            navKey={navKeys[navKeys.length - 1]}
            isLast={true}
            activeColor={bc.breadcrumbActiveColor}
            inactiveColor={bc.breadcrumbInactiveColor}
          />
        </>
      )}

      {(!isSmallScreen || breadCrumbs.length <= 2) &&
        breadCrumbs.map((crumb, index) => {
          const isLastCrumb = index === breadCrumbs.length - 1;
          return (
            <React.Fragment key={`crumb-${index}`}>
              <BreadcrumbItem
                crumb={crumb}
                navKey={navKeys[index]}
                isLast={isLastCrumb}
                activeColor={bc.breadcrumbActiveColor}
                inactiveColor={bc.breadcrumbInactiveColor}
              />
              {!isLastCrumb && (
                <ChevronIcon color={bc.breadcrumbInactiveColor} />
              )}
            </React.Fragment>
          );
        })}
    </nav>
  );
};

interface BreadcrumbItemProps {
  crumb: string;
  navKey: string | null;
  isLast: boolean;
  activeColor: string;
  inactiveColor: string;
}

const PATH_LABEL_OVERRIDES: Record<string, string> = {
  'tpms-sensors': 'TPMS Sensors',
  'new-road-condition': 'Road Condition Summary · scored data',
};

const BreadcrumbItem = ({
  crumb,
  navKey,
  isLast,
  activeColor,
  inactiveColor,
}: BreadcrumbItemProps) => {
  const { t } = useTranslation();
  /* Replace - with space and capitalize first letter */
  const rawFormattedCrumb =
    PATH_LABEL_OVERRIDES[crumb] ??
    capitalizeFirstLetter(safeReplaceBredcrumb(crumb, '-', ' '));
  const formattedCrumb = navKey
    ? t(navKey, rawFormattedCrumb)
    : rawFormattedCrumb;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        maxWidth: '320px',
        position: 'relative',
      }}
      title={formattedCrumb}
      aria-current={isLast ? 'page' : undefined}
    >
      <p
        className={isLast ? 'poppins-semibold' : 'poppins-medium'}
        style={{
          color: isLast ? activeColor : inactiveColor,
          fontSize: '10px',
          margin: 0,
          padding: '2px 4px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          transition: 'color 0.2s ease',
        }}
      >
        {formattedCrumb}
      </p>
    </div>
  );
};
